import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphDashboard_AtlassianSearchPage$key } from './__generated__/searchGraphDashboard_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchGraphDashboard = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphDashboard_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const partial = useFragment(
		graphql`
			fragment searchGraphDashboard_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				title
				url
				lastModifiedDate
				excerpt
				entity {
					... on ExternalDashboard {
						id
						url
						workspaceName
						owners {
							thirdPartyUser {
								name
							}
						}
					}
				}
			}
		`,
		node,
	);

	const { url, entity, title, lastModifiedDate, excerpt } = partial;

	const lastModifiedComponent = <LastModified lastModified={lastModifiedDate} />;

	const getOwnerName = () => {
		if (!entity?.owners || !Array.isArray(entity.owners) || entity.owners.length === 0) {
			return undefined;
		}
		return entity.owners[0]?.thirdPartyUser?.name;
	};

	const workspaceName = entity?.workspaceName;

	const ownerName = getOwnerName();

	const headerMetadataParts = [
		formatMessage(messages.eventLabel),
		['workspaceName', workspaceName],
		...(workspaceName && ownerName && workspaceName === ownerName
			? []
			: ownerName
				? [['owner', ownerName]]
				: []),
		['last-modified', lastModifiedComponent],
	];

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			icon={icon}
			description={processExcerpt(excerpt)}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			position={position}
			partial={partial}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};
